import { ParameterInputType, ParameterSubjectType, ParameterType, SelectableParameter, SINGLE_VALUE_INPUT_TYPES } from "@/types/parameterTypes";
import { useShallow } from "zustand/react/shallow";
import { useParameterStore } from "../store/ParameterStore";
import { findReferencedParameters } from "../utils/parseReferences";
import { Parameter } from "@/backend/models/parameter";
import { Operation } from "@/backend/models/operation";
import { ObjectKeyProperty } from "@/backend/models/objectInstance";
import { I18n } from "@/i18n/I18n";
import { useTreeStore } from "../store/TreeStore";
import { DataTypes, ItemReferenceParams } from "@/types/itemReferenceTypes";
import { CUSTOM_FIELD_TYPES } from "@/types/customFieldTypes";
import { ContainerTreeObject } from "../types/navigationTypes";

export interface Scaffoldable {
  rangeParameterId: string;
  skipParameterId: string;
  skipKeyId: string;
  parentId: string;
  id: string;
}

export function useParseProcedureReferences(item: { rangeParameterId: string, parentId: string }) {
  const procedureParams = useParameterStore(useShallow(state => state.parameters.filter(param => param.subjectType === ParameterSubjectType.PROCEDURE)));
  const containerLookup = useTreeStore(state => state.containerLookup);
  const collectRangedParameters = (containerId: string): string[] => {
    const container = containerLookup[containerId];
    if (!container) return [];

    const collectedParameters = collectRangedParameters(container.parentId);
    if (container.container.rangeParameterId) collectedParameters.push(container.container.rangeParameterId);

    return collectedParameters;
  };

  const rangedParameters = item ? [item.rangeParameterId, ...collectRangedParameters(item.parentId)] : [];

  const nonListParams = procedureParams.filter(param => !param.hasMultipleValues() || rangedParameters.includes(param.id));
  return (value: string) => {
    const result = findReferencedParameters(value, nonListParams);
    if (result) {
      const matchingProcedureParam = procedureParams.find(param => param.identifier === result.foundIdentifier && !nonListParams.includes(param));
      if (matchingProcedureParam) {
        return I18n.get('parameterNotRangedOver', { match: result.foundIdentifier });
      }
      return result.error;
    }

    return null;
  };
}

export function useBuildCurrentParameterResponse(parameter?: Parameter, objectKeyId?: string) {
  const operations = useTreeStore(state => state.operations);
  return buildCurrentParameterResponse(operations, parameter, objectKeyId);
}

export function buildCurrentParameterResponse(operations: Operation[], parameter?: Parameter, objectKeyId?: string) {
  if (!parameter) return undefined;

  const property = parameter.propertyKeys.find(({ keyId }) => keyId === objectKeyId);
  return buildSelectableParameter(parameter, operations, property);
}

export function useGetParametersToAssignFromInput(item: Scaffoldable, subjectId: string) {

  const parameters = useParameterStore(state => state.parameters);
  const parameterHasCorrectTypeForSubject = useParameterHasCorrectTypeForSubject(item, subjectId);
  const operations = useTreeStore(state => state.operations);

  const parametersForSubject = parameters.filter(parameter =>
    parameterHasCorrectTypeForSubject(parameter)
  );

  return buildSelectableParameters(parametersForSubject, operations);
}

function useParameterHasCorrectTypeForSubject(subjectOperation: Scaffoldable, subjectId: string) {
  const operations = useTreeStore(state => state.operations);
  const containerLookup = useTreeStore(state => state.containerLookup);

  return (parameter: Parameter) => {
    if (parameter.type === ParameterType.INPUT || parameter.type === ParameterType.CONFIG)  {
      if (parameter.hasRootSubject()) return true;
      if (parameter.subjectId === subjectId) return true;
      return false;
    }

    if (!(subjectOperation instanceof Operation)) return false;

    if (parameter.subjectType === ParameterSubjectType.STEP) {
      const scenarioForSubjectOperation = containerLookup[subjectOperation.parentId];
      const stepForSubjectOperation = containerLookup[scenarioForSubjectOperation.parentId];
      const stepForParameter = containerLookup[parameter.subjectId];
      if (stepForParameter?.container.isScaffold !== stepForSubjectOperation?.container.isScaffold) return false;
      return inCorrectPosition(stepForSubjectOperation, stepForParameter);
    }

    const operation = operations.find(op => op.id === parameter.subjectId);

    if (!operation) return false;
    if (operation.parentId !== subjectOperation.parentId) return false;

    return operation.position < subjectOperation.position;
  };
}

export function inCorrectPosition(subject: ContainerTreeObject, possibleReferenceSubject: ContainerTreeObject) {
  if (subject?.stepPosition === undefined || possibleReferenceSubject?.stepPosition === undefined) return false;
  return possibleReferenceSubject.stepPosition < subject.stepPosition;
}

export function buildSelectableParameters(parameters: Parameter[], operations: Operation[]) {
  const finalParameters: SelectableParameter[] = [];

  parameters.forEach(parameter => {
    finalParameters.push(buildSelectableParameter(parameter, operations));
    parameter.propertyKeys.forEach(property => {
      finalParameters.push(buildSelectableParameter(parameter, operations, property));
    });
  });

  return finalParameters;
}

function buildSelectableParameter(parameter: Parameter, operations: Operation[], property?: ObjectKeyProperty) {
  const subjectOp = operations.find(op => op.id === parameter.subjectId);
  const paramTitle =  parameter.title || subjectOp?.title || (subjectOp && I18n.get('operationAtIndex', { index: `${subjectOp.position + 1}` })) || '';
  const selectableParameter: SelectableParameter = {
    ...parameter,
    title: paramTitle,
    fieldType: parameter.fieldType,
    inputType: parameter.inputType,
    subjectType: parameter.subjectType,
  };

  if (property) {
    selectableParameter.keyId = property.keyId;
    selectableParameter.title = `${paramTitle}.${property.title || property.key}`;
    selectableParameter.fieldType = property.type;
    selectableParameter.inputType = ParameterInputType.FREE_FORM;
  }

  return selectableParameter;
}

export function useParameterHasCorrectInputType(consumerInputType: ParameterInputType, item?: Scaffoldable) {
  const containerLookup = useTreeStore(state => state.containerLookup);

  const collectRangedParameters = (containerId: string): string[] => {
    const container = containerLookup[containerId];
    if (!container) return [];

    const collectedParameters = collectRangedParameters(container.parentId);
    if (container.container.rangeParameterId) collectedParameters.push(container.container.rangeParameterId);

    return collectedParameters;
  };

  const rangedParameters = item ? [item.rangeParameterId, ...collectRangedParameters(item.parentId)] : [];

  return (source: SelectableParameter) => {
    const sourceInputType = source.inputType;
    if (consumerInputType === ParameterInputType.FREE_FORM) {
      return sourceInputType === ParameterInputType.FREE_FORM || sourceInputType === ParameterInputType.SINGLE_CHOICE || rangedParameters.includes(source.id);
    }
    if (consumerInputType === ParameterInputType.SINGLE_CHOICE) {
      return sourceInputType === ParameterInputType.SINGLE_CHOICE;
    }
    if (consumerInputType === ParameterInputType.MULTIPLE_CHOICE) {
      return sourceInputType === ParameterInputType.MULTIPLE_CHOICE;
    }
    if (consumerInputType === ParameterInputType.LIST) {
      return sourceInputType === ParameterInputType.LIST || sourceInputType === ParameterInputType.MULTIPLE_CHOICE;
    }

    if (consumerInputType === ParameterInputType.TABLE) {
      return sourceInputType === ParameterInputType.TABLE || sourceInputType === ParameterInputType.LIST;    
    }

    return false;
  };
}

export function useGetParametersToAssignFromOutput(subjectId: string) {
  const containerLookup = useTreeStore(state => state.containerLookup);
  const parent = containerLookup[subjectId];

  const parameters = useParameterStore(useShallow(state => state.parameters.filter(
    param => param.type === ParameterType.OUTPUT
    && param.subjectId !== subjectId
    && containerLookup[param.subjectId]?.container.isScaffold === parent?.container.isScaffold
    && inCorrectPosition(parent, containerLookup[param.subjectId])
  )));

  return parameters;
}

export function useGetRefAndParameterByConsumer(dataConsumerId: string) {
  const currentReference = useParameterStore(useShallow(state => state.references.find(ref => ref.dataConsumerId === dataConsumerId && !ref.isInterpolated)));
  const currentParameter = useParameterStore(useShallow(state => state.parameters.find(param => param.id === currentReference?.dataSourceId)));
  const currentSelectableParameter = useBuildCurrentParameterResponse(currentParameter, currentReference?.sourceObjectKeyId);
  return { currentParameter: currentSelectableParameter, currentReference };
}

export type SelectedRefAndParameter = {
  currentParameter: SelectableParameter;
  currentReference: ItemReferenceParams;
};

export function useGetAllRefsAndParametersByConsumer(dataConsumerId: string): SelectedRefAndParameter[] {
  const currentReferences = useParameterStore(useShallow(state => state.references.filter(ref => ref.dataConsumerId === dataConsumerId && !ref.isInterpolated)));
  const parameters = useParameterStore(state => state.parameters);
  const operations = useTreeStore(state => state.operations);

  return currentReferences.map(reference => {
    const sourceParameter = parameters.find(param => param.id === reference.dataSourceId);
    const currentParameter = buildCurrentParameterResponse(operations, sourceParameter, reference.sourceObjectKeyId);
    return currentParameter ? { currentParameter, currentReference: reference } : undefined;
  }).filter(selection => selection !== undefined);
}

export function useCreateSourceReference(subjectId: string) {
  const createReference = useParameterStore(state => state.createReference);

  return (parameter: SelectableParameter) => {
    void createReference({
      dataConsumerId: subjectId,
      dataConsumerType: DataTypes.Parameter,
      dataSourceId: parameter.id,
      dataSourceType: DataTypes.Parameter,
      sourceObjectKeyId: parameter.keyId
    });
  };
}

export function useGetParametersToAssignToOutput(operation: Operation, fieldType: CUSTOM_FIELD_TYPES, parameterInputType: ParameterInputType) {
  const siblingOperationIds = useTreeStore(useShallow((state) => state.getStoreOperationsForParent(operation.parentId).map(op => op.id)));
  const operationParameterIds = useParameterStore(useShallow(state => state.parameters.filter(param => siblingOperationIds.includes(param.subjectId)).map(param => param.id)));
  const currentSourceRefs = [...siblingOperationIds, ...operationParameterIds];
  const parameterReferences = useParameterStore(useShallow(state => state.references.filter(ref => ref.dataConsumerType === DataTypes.Parameter && currentSourceRefs.includes(ref.dataSourceId))));
  const scenario = useTreeStore(useShallow(state => state.containerLookup[operation.parentId]));
  const operations = useTreeStore(state => state.operations);

  const selectableParameters = useParameterStore(useShallow(state => state.parameters.filter(parameter =>
    parameter.type === ParameterType.OUTPUT
    && parameter.subjectId === scenario.parentId
    && (parameter.inputType === parameterInputType || (SINGLE_VALUE_INPUT_TYPES.includes(parameterInputType) && (parameter.inputType === ParameterInputType.TABLE || parameter.inputType === ParameterInputType.LIST)))
  )));

  const finalParameters: SelectableParameter[] = [];

  selectableParameters.forEach(parameter => {
    if (!parameterReferences.some(reference => reference.dataConsumerId === parameter.id)) {
      finalParameters.push(buildSelectableParameter(parameter, operations));
    }
    parameter.propertyKeys.forEach(property => {
      if (!parameterReferences.some(reference => reference.dataConsumerId === parameter.id && reference.consumerObjectKeyId === property.keyId)) {
        finalParameters.push(buildSelectableParameter(parameter, operations, property));
      }
    });
  });

  return finalParameters.filter(parameter => parameter.fieldType === fieldType);
}

export function useCreateConsumerReference(subjectId: string) {
  const createReference = useParameterStore(state => state.createReference);

  return (parameter: SelectableParameter) => {
    void createReference({
      dataSourceId: subjectId,
      dataSourceType: DataTypes.Parameter,
      dataConsumerId: parameter.id,
      dataConsumerType: DataTypes.Parameter,
      consumerObjectKeyId: parameter.keyId
    });
  };
}

export function useGetRefAndParameterBySource(dataSourceId: string) {
  const parameters = useParameterStore(state => state.parameters);
  const operations = useTreeStore(state => state.operations);

  const currentReferences = useParameterStore(useShallow(state => state.references.filter(reference => reference.dataSourceId === dataSourceId)));
  for (let i = 0; i < currentReferences.length; i += 1) {
    const reference = currentReferences[i];
    const currentParameter = parameters.find(parameter => parameter.id === reference.dataConsumerId);

    if (currentParameter?.subjectType !== ParameterSubjectType.VALIDATION) {
      const currentSelectableParameter = buildCurrentParameterResponse(operations, currentParameter, reference.sourceObjectKeyId || reference.consumerObjectKeyId);
      return { currentParameter: currentSelectableParameter, currentReference: reference };
    }
  }

  return { currentParameter: undefined, currentReference: undefined };
}


export function useGetParamsForRepeatingStepCycle() {
  const containerLookup = useTreeStore(state => state.containerLookup);
  const parameters = useParameterStore(useShallow(state => state.parameters.filter(parameter => {
    if (!parameter.hasMultipleValues()) return false;

    if (parameter.subjectType === ParameterSubjectType.PROCEDURE && parameter.type === ParameterType.CONFIG) return true;

    if (parameter.type !== ParameterType.OUTPUT) return false;

    if (parameter.subjectType === ParameterSubjectType.STEP) {
      const step = containerLookup[parameter.subjectId];
      if (!step?.container.isScaffold) return false;
      if (step.container.isRepeatingStep()) return true;
    }

    return false;
  })));
  return parameters;
}

export function useParameterHasCorrectSubject(owningParameter: Parameter, owningContainerId: string) {
  const operations = useTreeStore(state => state.operations);
  const containerLookup = useTreeStore(state => state.containerLookup);
  const owningContainer = containerLookup[owningContainerId];

  return (parameter: Parameter) => {
    if (!owningContainer) return false;

    if (parameter.subjectType === ParameterSubjectType.OPERATION) {
      if (owningParameter.type === ParameterType.INPUT && owningParameter.subjectType === ParameterSubjectType.STEP) return false;
      const operation = operations.find(op => op.id === parameter.subjectId);
      if (!operation) return false;
      if (owningContainer.container.isScaffold !== operation.isScaffold) return false;
      const scenario = containerLookup[operation.parentId];
      return scenario?.parentId === owningContainerId;
    }

    if (parameter.subjectType === ParameterSubjectType.STEP) {
      const parameterContainer = containerLookup[parameter.subjectId];
      if (owningContainer.container.isScaffold !== parameterContainer?.container.isScaffold) return false;
      if (owningParameter.subjectType === ParameterSubjectType.VALIDATION && parameter.type === ParameterType.OUTPUT) return false;
      if (owningParameter.type === ParameterType.INPUT && owningParameter.subjectType === ParameterSubjectType.STEP) {
        return inCorrectPosition(owningContainer, parameterContainer);
      }
      return parameter.subjectId === owningContainerId;
    }

    return false;
  };
}

export function useGetAllInputParametersForOperation(operation: Operation) {
  const containerLookup = useTreeStore(state => state.containerLookup);
  const scenario = containerLookup[operation.parentId];
  const otherOperations = useTreeStore(useShallow(state => state.getStoreOperationsForParent(operation.parentId)));
  return useParameterStore(useShallow(state => state.parameters.filter(parameter => {
    if (parameter.subjectId === operation.rootId) return true;
    if (parameter.subjectId === scenario?.parentId && parameter.type === ParameterType.INPUT) return true;
    if (otherOperations.find(op => op.id === parameter.subjectId) && parameter.type === ParameterType.FIELD) return true;
    return false;
  }
  )));
}
